/**
 * Integrytas Group - CSM: the reporting line (csm_user_master.manager_user_id).
 *
 * The CLIENT-side twin of the SQL helper `csm_user_reports_to(manager, user)`
 * (20260826000000_csm_delegated_deactivate_mimic.sql). Both answer the same
 * question — "is this user anywhere beneath that one?" — and both must agree,
 * or the UI offers an action the server then refuses.
 *
 * This is NOT the authorization decision. The server's RLS policies and the
 * csm_user_master guard trigger are, and they resolve the real signed-in
 * identity from the JWT. This exists so the UI can avoid offering an action
 * that can only end in an error (§17.A, the same reasoning as `hasAccess`).
 *
 * Deliberately mirrors the SQL in the two ways that matter:
 *   - a user is never their own manager, however the rows are shaped;
 *   - a manager_user_id cycle terminates instead of hanging. Nothing in the
 *     schema forbids A -> B -> A, so both walks must survive one.
 */

/** The only shape either helper needs from a user row. */
export interface ReportingLineRow {
  readonly user_id: number;
  readonly manager_user_id: number | null;
}

/**
 * Every user beneath `rootUserId` in the reporting line, to any depth.
 * The root itself is NOT included — you do not report to yourself, and that
 * exclusion is what stops a delegate acting on their own record.
 *
 * Walks down breadth-first over a manager -> reports index, visiting each user
 * at most once, so a cycle costs one pass rather than looping forever.
 */
export function collectReportingSubtree(
  rows: readonly ReportingLineRow[],
  rootUserId: number,
): ReadonlySet<number> {
  const reportsByManager = new Map<number, number[]>();
  for (const row of rows) {
    if (row.manager_user_id == null) continue;
    const siblings = reportsByManager.get(row.manager_user_id);
    if (siblings) siblings.push(row.user_id);
    else reportsByManager.set(row.manager_user_id, [row.user_id]);
  }

  const subtree = new Set<number>();
  const queue = [...(reportsByManager.get(rootUserId) ?? [])];
  while (queue.length > 0) {
    const userId = queue.shift() as number;
    // Already-seen guard: doubles as the cycle guard, and a self-referencing
    // row can never add the root back into its own subtree.
    if (userId === rootUserId || subtree.has(userId)) continue;
    subtree.add(userId);
    const children = reportsByManager.get(userId);
    if (children) queue.push(...children);
  }
  return subtree;
}
